import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardList, Plus, Search, LayoutGrid, List, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { mccGet, mccPost, mccPatch, mccDelete } from './mccApi';
import { SectionHeader, EmptyState, StatusPill, PlatformIcon, Avatar, PriorityPill, DueBadge } from './mccUi';
import { REQUEST_STAGES, PLATFORMS, statusTone, humanDate } from './mccConstants';
import MccKanban from './MccKanban';
import MccCreateDialog from './MccCreateDialog';
import MccStatusGroup from './MccStatusGroup';

export default function MccRequests({ team, onProjectCreated }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');
  const [filters, setFilters] = useState({ status: '', platform: '', q: '' });
  const [openDialog, setOpenDialog] = useState(false);
  const nameOf = (uid) => (team.find((t) => t.id === uid) || {}).name || uid;

  const load = async () => {
    setLoading(true);
    try {
      const res = await mccGet('/marketing/requests', {
        status: filters.status, platform: filters.platform, q: filters.q,
      });
      setItems(res.items || []);
    } catch (e) { toast.error(e.message || 'Failed to load requests'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status, filters.platform]);

  const filtered = useMemo(() => {
    if (!filters.q) return items;
    const ql = filters.q.toLowerCase();
    return items.filter((i) =>
      (i.title || '').toLowerCase().includes(ql) ||
      (i.description || '').toLowerCase().includes(ql)
    );
  }, [items, filters.q]);

  const convert = async (r) => {
    try {
      const res = await mccPost(`/marketing/requests/${r.id}/convert-to-project`);
      toast.success('Request converted to content project');
      onProjectCreated?.(res.id);
      await load();
    } catch (e) { toast.error(e.message || 'Convert failed'); }
  };

  const advance = async (r, status) => {
    try { await mccPatch(`/marketing/requests/${r.id}`, { status }); toast.success(`Moved to ${statusTone(status).label}`); await load(); }
    catch (e) { toast.error(e.message || 'Update failed'); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete request "${r.title}"?`)) return;
    try { await mccDelete(`/marketing/requests/${r.id}`); await load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  return (
    <div className="p-6 space-y-4">
      <SectionHeader
        title="Marketing Requests"
        subtitle="Brokers, managers and team members submit requests for videos, posts, ads, emails, website updates and branding here."
        icon={ClipboardList}
        right={<Button onClick={() => setOpenDialog(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="mcc-request-create">
          <Plus className="w-4 h-4 mr-1" /> New Request
        </Button>}
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input className="pl-8 w-64" placeholder="Search requests…" value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
        </div>
        <Select value={filters.status || '__all__'} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === '__all__' ? '' : v }))}>
          <SelectTrigger className="h-9 w-44"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            {REQUEST_STAGES.map((s) => <SelectItem key={s} value={s}>{statusTone(s).label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.platform || '__all__'} onValueChange={(v) => setFilters((f) => ({ ...f, platform: v === '__all__' ? '' : v }))}>
          <SelectTrigger className="h-9 w-40"><SelectValue placeholder="Platform" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All platforms</SelectItem>
            {PLATFORMS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex rounded-md border border-slate-200 overflow-hidden">
          <button onClick={() => setView('kanban')} className={`px-2.5 py-1.5 ${view === 'kanban' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}><LayoutGrid className="w-3.5 h-3.5" /></button>
          <button onClick={() => setView('list')} className={`px-2.5 py-1.5 ${view === 'list' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}><List className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 p-8 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No marketing requests yet"
          body="Brokers can submit content, video, campaign, website and branding requests from here."
          cta={<Button onClick={() => setOpenDialog(true)}>Submit a request</Button>} />
      ) : view === 'kanban' ? (
        <MccKanban stages={REQUEST_STAGES} items={filtered} variant="request" nameOf={nameOf}
          onOpen={(req) => actOnRequest(req, advance, convert, del)}
          onMove={(item, newStatus) => advance(item, newStatus)} />
      ) : (
        <div className="space-y-3">
          {REQUEST_STAGES.map((stage) => {
            const rows = filtered.filter((r) => r.status === stage);
            if (rows.length === 0) return null;
            return (
              <MccStatusGroup key={stage} status={stage} count={rows.length}>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/60 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Title</th>
                      <th className="px-3 py-2 text-left">Requested by</th>
                      <th className="px-3 py-2 text-left">Platform</th>
                      <th className="px-3 py-2 text-left">Assigned</th>
                      <th className="px-3 py-2 text-left">Due</th>
                      <th className="px-3 py-2 text-left">Priority</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => actOnRequest(r, advance, convert, del)}>
                        <td className="px-3 py-2 font-medium text-slate-900">{r.title}</td>
                        <td className="px-3 py-2 text-slate-700">{r.requested_by_name || '—'}</td>
                        <td className="px-3 py-2">{r.primary_platform ? <PlatformIcon platform={r.primary_platform} size={18} /> : '—'}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            {(r.assigned_to || []).slice(0, 3).map((uid) => <Avatar key={uid} name={nameOf(uid)} size={18} />)}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-[12px]"><DueBadge dueIso={r.due_date} /></td>
                        <td className="px-3 py-2"><PriorityPill priority={r.priority} /></td>
                        <td className="px-3 py-2 text-right">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); convert(r); }} data-testid={`mcc-request-convert-${r.id}`}>
                            Convert <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </MccStatusGroup>
            );
          })}
        </div>
      )}

      <MccCreateDialog open={openDialog} onClose={() => setOpenDialog(false)} kind="request" team={team} onCreated={load} />
    </div>
  );
}

function actOnRequest(r, advance, convert, del) {
  const choice = window.prompt(
    `Request: ${r.title}\n\nType:\n- ACK to acknowledge\n- START to move to in progress\n- DECLINE to decline\n- CONVERT to create a content project\n- (Enter) to cancel`,
    ''
  );
  if (!choice) return;
  const c = choice.trim().toUpperCase();
  if (c === 'ACK') advance(r, 'acknowledged');
  else if (c === 'START') advance(r, 'in_progress');
  else if (c === 'DECLINE') advance(r, 'declined');
  else if (c === 'CONVERT') convert(r);
}
